import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom'
import StockContext from '../../../../contexts/StockContext'
import TableFiltersContext from '../../../../contexts/TableFiltersContext'
import ModalContext from '../../../../contexts/ModalContext'
import SectionContext from '../../../../contexts/SectionContext'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import ApiService from '../../../../services/ApiService'
import axios from '../../../../config/axios'
import { useTenant } from '../../../../contexts/TenantContext'
import { Eye, Pencil, Copy, ArrowLeftRight, Power, Check, X, Trash2 } from 'lucide-react'
import { StandardDataTable, useColumnsState, orderVisibleColumns, RowActionsMenu } from '../../../../components/StandardPage'
import ConfirmationModal from '../../../../components/ModalConfirmation'
import { BulkHeaderLine, BulkHeaderTd } from '../../../../components/styles/table-style'
import InfiniteSelect from '../../../../components/InfiniteSelect'
import { ncmCatalogoEndpoint } from '../../../../utils/ncmCatalogo'
import LoadingComponent from '../../../../components/LoadingComponent'
import { formatReceivedDate } from './utils'
import { StockOutletType } from '..'
import { TypeMainOutlet } from '../..'
import { buildStockColumns, stockScreenKey } from './stockColumns'

const StockTable: React.FC = () => {
  const { setStock, stockList, updateStockList, refresh, hasMoreStock, isLoading } =
    useContext(StockContext)
  const { stockFilters } = useContext(TableFiltersContext)
  const { search } = useOutletContext<TypeMainOutlet>()
  const { setModalActive, setModalSection, setItIsUpdate, setIsVariation, stockMode, setStockMode } =
    useContext(ModalContext)
  const { setFeedback } = useContext(FeedbackContext)
  const { isFashionTenant } = useTenant()
  const isFashion = isFashionTenant || stockMode === 'fashion'
  const { setQuantity, quantity } = useOutletContext<StockOutletType>()
  const { setStockSection } = useContext(SectionContext)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const stockIdParam = searchParams.get('stockId') || searchParams.get('id')
  const stepParam = searchParams.get('step')
  const [openedStockId, setOpenedStockId] = useState<number | null>(null)

  const [selectedStockIds, setSelectedStockIds] = useState<Set<number>>(new Set())
  const [bulkDraft, setBulkDraft] = useState<Record<string, any>>({})
  const [isBulkSaving, setIsBulkSaving] = useState<boolean>(false)
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null)
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState<boolean>(false)
  const [bulkInactivateOpen, setBulkInactivateOpen] = useState<boolean>(false)

  const columns = useMemo(() => buildStockColumns({ isFashion, navigate }), [isFashion, navigate])
  const { columnsState, handleColumnsChange, handleRestoreColumns } = useColumnsState(
    stockScreenKey(isFashion),
    columns,
  )

  useEffect(() => {
    setStockSection('stock')
  }, [setStockSection])

  const [totalStockCount, setTotalStockCount] = useState<number | undefined>(
    undefined,
  )

  useEffect(() => {
    let mounted = true
    const fetchTotal = async () => {
      try {
        const parameters: any = {}
        if (stockFilters && stockFilters.length > 0) parameters.filters = JSON.stringify(stockFilters)
        if (search && search.trim().length > 0) parameters.global = search
        const resp = await ApiService.stock.count(parameters)
        if (mounted && resp && resp.data != null) {
          const value =
            typeof resp.data === 'number'
              ? resp.data
              : resp.data.total ?? resp.data.totalItems ?? undefined
          setTotalStockCount(value)
        }
      } catch (e) {
        console.error(e)
      }
    }

    fetchTotal()

    return () => {
      mounted = false
    }
  }, [stockFilters, search, refresh])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      updateStockList(quantity, stockFilters, search)
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [quantity, stockFilters, search, refresh])

  const normalizeStockItem = (item: any) => {
    const newItem = { ...item }
    if (newItem.receivedDate) {
      newItem.receivedDate = formatReceivedDate(newItem.receivedDate)
    }
    return {
      ...newItem,
      stockStatus: newItem.stockStatus ?? newItem.status ?? '',
      itemColorId: newItem.itemColorId ?? null,
      color: newItem.color ?? '',
      pantoneCode: newItem.pantoneCode ?? '',
      pantoneHex: newItem.pantoneHex ?? '',
    }
  }

  const handleNewModal = (item: any) => {
    setStock(normalizeStockItem(item))
    if (isFashionTenant) setStockMode('fashion')
    setModalActive(true)
    setModalSection('newModal')
  }

  useEffect(() => {
    if (!stockIdParam) {
      setOpenedStockId(null)
      return
    }

    const parsedId = Number(stockIdParam)
    if (Number.isNaN(parsedId) || parsedId <= 0 || openedStockId === parsedId) {
      return
    }

    const targetSection = stepParam === '3' ? 'thirdCustomAdd' : (stepParam === '2' ? 'secondCustomAdd' : null)

    const itemInList = stockList.find((s) => Number(s.id) === parsedId)
    if (itemInList) {
      setOpenedStockId(parsedId)
      if (targetSection) {
        handleEdit(itemInList, targetSection)
      } else {
        handleNewModal(itemInList)
      }
      return
    }

    let isMounted = true
    const fetchAndOpenStock = async () => {
      try {
        const resp = await ApiService.stock.findAll({
          filters: JSON.stringify([{ id: 'id', value: String(parsedId) }]),
          size: 1,
        })
        const items = resp?.data ? (Array.isArray(resp.data) ? resp.data : resp.data.items || []) : []
        const found = items.find((s: any) => Number(s.id) === parsedId) || items[0]
        if (isMounted && found) {
          setOpenedStockId(parsedId)
          if (targetSection) {
            handleEdit(found, targetSection)
          } else {
            handleNewModal(found)
          }
        }
      } catch (err) {
        console.error(err)
      }
    }

    fetchAndOpenStock()

    return () => {
      isMounted = false
    }
  }, [stockIdParam, stepParam, openedStockId, stockList])

  const handleEdit = (item: any, section: 'firstCustomAdd' | 'secondCustomAdd' | 'thirdCustomAdd' = 'firstCustomAdd') => {
    setStock(normalizeStockItem(item))
    if (isFashionTenant) setStockMode('fashion')
    setItIsUpdate(true)
    setModalActive(true)
    setModalSection(section)
  }

  const handleCreateVariation = (item: any) => {
    setStock({ ...normalizeStockItem(item), code: '', color: '' })
    if (isFashionTenant) setStockMode('fashion')
    setIsVariation(true)
    setItIsUpdate(true)
    setModalActive(true)
    setModalSection('firstCustomAdd')
  }

  const handleNewMovement = (item: any) => {
    setStock(normalizeStockItem(item))
    if (isFashionTenant) setStockMode('fashion')
    setItIsUpdate(true)
    setModalActive(true)
    setModalSection('modalEntryOrExit')
  }

  const handleSwitchActivation = async (item: any) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/stock/activateStock`,
        {
          id: Number(item.id),
          isActivated: !item.isActivated,
        },
      )
      setFeedback({
        isOpen: true,
        message: `Item ${!item.isActivated ? 'ativado' : 'inativado'} com sucesso!`,
        statusOk: true,
      })
      updateStockList(quantity, stockFilters, search)
    } catch {
      setFeedback({
        isOpen: true,
        message: 'Erro ao mudar status de ativação.',
        statusOk: false,
      })
    }
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    try {
      await ApiService.stock.delete(Number(deleteTarget.id))
      setFeedback({
        isOpen: true,
        message: 'Item de estoque excluído com sucesso!',
        statusOk: true,
      })
      updateStockList(quantity, stockFilters, search)
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao excluir item de estoque.'
      setFeedback({
        isOpen: true,
        message: Array.isArray(errorMessage) ? errorMessage[0] : errorMessage,
        statusOk: false,
      })
    } finally {
      setDeleteTarget(null)
    }
  }

  const fetchAllStockRows = async () => {
    try {
      const parameters: any = { size: 100000 }
      if (stockFilters && Array.isArray(stockFilters) && stockFilters.length > 0) parameters.filters = JSON.stringify(stockFilters)
      if (search && search.trim().length > 0) parameters.global = search
      const response = await ApiService.stock.findAll(parameters)
      const items = Array.isArray(response?.data) ? response.data : response?.data?.items || []
      return items.map((item: any) => normalizeStockItem(item))
    } catch {
      return stockList
    }
  }

  const fetchAllStockRecords = async () => {
    try {
      const response = await ApiService.stock.findAll({ size: 100000 })
      const items = Array.isArray(response?.data) ? response.data : response?.data?.items || []
      return items.map((item: any) => normalizeStockItem(item))
    } catch {
      return stockList
    }
  }

  const handleConfirmBulkDelete = async () => {
    if (selectedStockIds.size === 0) return
    setIsBulkSaving(true)
    try {
      const ids = Array.from(selectedStockIds)
      for (const id of ids) {
        await ApiService.stock.delete(Number(id))
      }
      setFeedback({
        isOpen: true,
        message: `${ids.length} itens de estoque excluídos com sucesso!`,
        statusOk: true,
      })
      setSelectedStockIds(new Set())
      setBulkDraft({})
      updateStockList(quantity, stockFilters, search)
    } catch {
      setFeedback({
        isOpen: true,
        message: 'Erro ao excluir itens selecionados.',
        statusOk: false,
      })
    } finally {
      setIsBulkSaving(false)
      setBulkDeleteOpen(false)
    }
  }

  const handleConfirmBulkInactivate = async () => {
    if (selectedStockIds.size === 0) return
    setIsBulkSaving(true)
    try {
      const ids = Array.from(selectedStockIds)
      for (const id of ids) {
        await axios.put(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/stock/activateStock`,
          {
            id: Number(id),
            isActivated: false,
          },
        )
      }
      setFeedback({
        isOpen: true,
        message: `${ids.length} itens inativados com sucesso!`,
        statusOk: true,
      })
      setSelectedStockIds(new Set())
      setBulkDraft({})
      updateStockList(quantity, stockFilters, search)
    } catch {
      setFeedback({
        isOpen: true,
        message: 'Erro ao inativar itens selecionados.',
        statusOk: false,
      })
    } finally {
      setIsBulkSaving(false)
      setBulkInactivateOpen(false)
    }
  }

  const handleSelectRow = (id: string | number, selected: boolean) => {
    const numId = Number(id)
    setSelectedStockIds((prev) => {
      const next = new Set(prev)
      if (selected) {
        next.add(numId)
      } else {
        next.delete(numId)
      }
      return next
    })
  }

  const handleSelectAllRows = (selected: boolean) => {
    if (selected) {
      const allIds = stockList.map((item: any) => item.id)
      setSelectedStockIds(new Set(allIds))
    } else {
      setSelectedStockIds(new Set())
    }
  }

  const handleSaveBulkEdits = async () => {
    if (selectedStockIds.size === 0) return
    setIsBulkSaving(true)
    try {
      const idsToUpdate = Array.from(selectedStockIds)
      const activeEdits = Object.entries(bulkDraft).filter(([_, val]) => val !== undefined && val !== '')

      if (activeEdits.length === 0) {
        setFeedback({
          isOpen: true,
          message: 'Preencha ao menos uma alteração nos campos do cabeçalho antes de salvar.',
          statusOk: false,
        })
        setIsBulkSaving(false)
        return
      }

      let successCount = 0
      for (const id of idsToUpdate) {
        try {
          const payload: any = {}
          for (const [colId, val] of activeEdits) {
            if (colId === 'isActivated') {
              payload.isActivated = val === 'Ativo'
            } else if (colId === 'stockStatus') {
              payload.stockStatus = val
            } else if (colId === 'quantities_kg') {
              payload.totalWeight = val
              payload.quantities_kg = val
            } else if (colId === 'quantities_mm') {
              payload.lengthInMm = val
              payload.quantities_mm = val
            } else if (colId === 'quantities_un') {
              payload.quantity = val
              payload.quantities_un = val
            } else if (colId === 'quantities_m2') {
              payload.quantities_m2 = val
            } else if (colId === 'quantities_mlinear') {
              payload.quantities_mlinear = val
            } else if (colId === 'prices_kg') {
              payload.costPerKg = val
              payload.prices_kg = val
            } else if (colId === 'prices_mm') {
              payload.costPerMm = val
              payload.prices_mm = val
            } else if (colId === 'prices_un') {
              payload.price = val
              payload.prices_un = val
            } else if (colId === 'prices_m2') {
              payload.prices_m2 = val
            } else if (colId === 'prices_mlinear') {
              payload.prices_mlinear = val
            } else if (colId === 'ncm') {
              continue
            } else if (colId === 'ncmId') {
              payload.ncmId = Number(val)
            } else {
              payload[colId] = val
            }
          }
          await ApiService.stock.update(id, payload)
          successCount++
        } catch (err) {
          console.error(`Erro ao atualizar estoque #${id} em massa:`, err)
        }
      }

      setFeedback({
        isOpen: true,
        message: `${successCount} estoque(s) atualizado(s) em massa com sucesso!`,
        statusOk: true,
      })
      setSelectedStockIds(new Set())
      setBulkDraft({})
      updateStockList(quantity, stockFilters, search)
    } catch (e) {
      console.error('Erro ao salvar em massa:', e)
      setFeedback({
        isOpen: true,
        message: 'Erro ao processar alteração em massa.',
        statusOk: false,
      })
    } finally {
      setIsBulkSaving(false)
    }
  }

  const getCompanyLabel = (item: any): string => {
    if (!item) return ''
    const baseName = (item.companyName || item.contactName || item.name || '').toString().trim()
    if (!baseName) return ''
    if (!item.isMain) return baseName
    const tipo = item.tipoEstabelecimento || (item.cnpj ? (item.cnpj.replace(/\D/g, '').substring(8, 12) === '0001' ? 'Matriz' : 'Filial') : '')
    return tipo ? `${baseName} - ${tipo}` : baseName
  }

  const handleSelectOption = (colId: string, option: any) => {
    const idField = `${colId}Id`
    if (!option) {
      setBulkDraft((prev) => {
        const next = { ...prev }
        delete next[colId]
        delete next[idField]
        return next
      })
      return
    }
    if (colId === 'ncm') {
      setBulkDraft((prev) => ({ ...prev, ncm: option.ncm ?? option.name, ncmId: option.id }))
      return
    }
    const isSupplierField = ['supplier', 'suppliers'].includes(colId)
    const name = getCompanyLabel(option) || option.name || option.type || option.heatNumber || String(option.id)
    if (isSupplierField) {
      setBulkDraft((prev) => {
        const currentList: Array<{ id: any; name: string }> = Array.isArray(prev.suppliers)
          ? prev.suppliers
          : typeof prev[colId] === 'string' && prev[colId].trim()
            ? prev[colId].split(',').map((s: string) => ({ id: s.trim(), name: s.trim() })).filter((s: any) => s.name)
            : []
        const nextList = currentList.some((s) => s.name === name || s.id === option.id)
          ? currentList
          : [...currentList, { id: option.id, name }]
        return {
          ...prev,
          [colId]: nextList.map((s) => s.name).join(', '),
          suppliers: nextList,
        }
      })
      return
    }
    setBulkDraft((prev) => ({
      ...prev,
      [colId]: name,
      ...(option.id ? { [idField]: option.id } : {}),
    }))
  }

  const renderBulkInput = (colId: string) => {
    const value = bulkDraft[colId] || ''
    const idValue = bulkDraft[`${colId}Id`]
    const isSupplierField = ['supplier', 'suppliers'].includes(colId)
    const selectedOption = value && !isSupplierField ? { id: idValue || value, name: value } : null

    if (colId === 'isActivated') {
      return (
        <select
          value={value}
          onChange={(e) => setBulkDraft((prev) => ({ ...prev, isActivated: e.target.value }))}
          style={{
            width: '100%',
            padding: '4px 6px',
            fontSize: '11px',
            borderRadius: '4px',
            border: '1px solid #0090d3',
            backgroundColor: '#fff',
            cursor: 'pointer',
          }}
        >
          <option value="">(Sem alteração)</option>
          <option value="Ativo">Ativo</option>
          <option value="Inativo">Inativo</option>
        </select>
      )
    }

    if (colId === 'certificateUrl' || colId === 'invoice' || colId === 'code' || colId === 'quantities' || colId === 'prices') {
      return null
    }

    if (colId === 'ncm') {
      return (
        <InfiniteSelect
          mode="crud"
          value={selectedOption}
          placeholder="Alterar..."
          searchPlaceholder="Buscar NCM..."
          onSelect={(option) => handleSelectOption(colId, option)}
          errorOptionsMessage="Código não existe na tabela oficial de NCM"
          showPantoneSwatch={false}
          fetchFunction={async (limit, offset, searchVal) => {
            const response = await ApiService.stockLookup.searchNcmCatalogo(searchVal, limit, offset)
            return ncmCatalogoEndpoint.transformResponse(response?.data)
          }}
          allowCreate={false}
          allowDelete={false}
        />
      )
    }

    const isLookup = [
      'material',
      'format',
      'finishing',
      'fabrication',
      'heatTreatment',
      'originCountry',
      'supplier',
      'suppliers',
      'stockStreet',
      'shelf',
      'stockLocation',
      'family',
      'stockStatus',
      'color',
      'heatNumber',
      'trackingNumber',
      'purchaseOrder',
    ].includes(colId)

    if (isLookup) {
      const rawSupplierList: Array<{ id: any; name: string }> = isSupplierField
        ? Array.isArray(bulkDraft.suppliers)
          ? bulkDraft.suppliers
          : typeof value === 'string' && value.trim()
            ? value.split(',').map((s: string) => ({ id: s.trim(), name: s.trim() })).filter((s: any) => s.name)
            : []
        : []

      return (
        <div style={{ minWidth: '140px', maxWidth: '240px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <InfiniteSelect
            mode="crud"
            value={selectedOption}
            placeholder={isSupplierField ? (rawSupplierList.length > 0 ? '+ Adicionar...' : 'Selecionar...') : 'Alterar...'}
            searchPlaceholder="Buscar ou criar..."
            onSelect={(option) => handleSelectOption(colId, option)}
            showPantoneSwatch={false}
            fetchFunction={async (_limit, _offset, search) => {
              let response: any
              if (colId === 'material') {
                response = await ApiService.stockLookup.getMaterials({ type: 'STOCK' })
              } else if (colId === 'format') {
                response = await ApiService.stockLookup.getFormats()
              } else if (colId === 'finishing') {
                response = await ApiService.stockLookup.getFinishings()
              } else if (colId === 'fabrication') {
                response = await ApiService.stockLookup.getFabrications()
              } else if (colId === 'heatTreatment') {
                response = await ApiService.stockLookup.getHeatTreatments()
              } else if (colId === 'originCountry') {
                response = await ApiService.stockLookup.getOriginCountries()
              } else if (isSupplierField) {
                response = await ApiService.companies.findAll({ query: search || '', companyType: 'Fornecedor' })
              } else if (colId === 'stockStreet') {
                response = await ApiService.stockLookup.getStockStreets()
              } else if (colId === 'shelf') {
                response = await ApiService.stockLookup.getShelfs()
              } else if (colId === 'stockLocation') {
                response = await ApiService.stockLookup.getStockLocations()
              } else if (colId === 'family') {
                response = await ApiService.stockLookup.getFamilies()
              } else if (colId === 'heatNumber') {
                response = await ApiService.stockLookup.getHeatNumbers()
              } else if (colId === 'color') {
                response = await ApiService.resources.getColors()
              } else if (colId === 'stockStatus') {
                response = await ApiService.stockLookup.getStockStatuses()
              } else if (colId === 'trackingNumber') {
                response = await ApiService.get('/backend/trackingNumber/list')
              } else if (colId === 'purchaseOrder') {
                try {
                  response = await ApiService.get('/backend/purchaseOrder')
                } catch {
                  response = { data: [] }
                }
              }

              const items = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : []
              const mapped = items
                .map((item: any, idx: number) => {
                  if (typeof item === 'string') return { id: item, name: item }
                  const name = getCompanyLabel(item) || item.name || item.number || item.type || item.ncm || item.status || item.heatNumber || item.purchaseOrder || ''
                  return {
                    id: item.id || name || `temp-${idx}`,
                    name: name,
                  }
                })
                .filter((item: any) => item.name && item.name.toLowerCase() !== 'null' && item.name.toLowerCase() !== 'undefined')

              return search
                ? mapped.filter((item: any) => item.name?.toLowerCase().includes(search.toLowerCase()))
                : mapped
            }}
            onItemCreate={async (name: string) => {
              let response: any
              const trimmedName = name.trim()
              if (colId === 'material') {
                response = await ApiService.stockLookup.createMaterial({ name: trimmedName, type: 'STOCK' })
              } else if (colId === 'format') {
                response = await ApiService.stockLookup.createFormat({ name: trimmedName })
              } else if (colId === 'finishing') {
                response = await ApiService.stockLookup.createFinishing({ name: trimmedName })
              } else if (colId === 'fabrication') {
                response = await ApiService.stockLookup.createFabrication({ name: trimmedName })
              } else if (colId === 'heatTreatment') {
                response = await ApiService.stockLookup.createHeatTreatment({ name: trimmedName })
              } else if (colId === 'originCountry') {
                response = await ApiService.stockLookup.createOriginCountry({ name: trimmedName })
              } else if (colId === 'stockStreet') {
                response = await ApiService.stockLookup.createStockStreet({ name: trimmedName })
              } else if (colId === 'shelf') {
                response = await ApiService.stockLookup.createShelf({ name: trimmedName })
              } else if (colId === 'stockLocation') {
                response = await ApiService.stockLookup.createStockLocation({ name: trimmedName })
              } else if (colId === 'family') {
                response = await ApiService.stockLookup.createFamily({ name: trimmedName })
              } else if (colId === 'heatNumber') {
                response = await ApiService.stockLookup.createHeatNumber({ name: trimmedName })
              } else if (colId === 'color') {
                response = await ApiService.resources.createColor({ name: trimmedName })
              } else if (colId === 'stockStatus') {
                response = await ApiService.stockLookup.createStockStatus({ name: trimmedName })
              } else if (colId === 'trackingNumber') {
                response = await ApiService.post('/backend/trackingNumber', { number: trimmedName })
              } else if (colId === 'purchaseOrder') {
                try {
                  response = await ApiService.post('/backend/purchaseOrder', { name: trimmedName })
                } catch {
                  response = { data: { id: trimmedName, name: trimmedName } }
                }
              }
              const resData = response?.data || {}
              return { id: resData.id || trimmedName, name: resData.name || resData.number || resData.type || trimmedName }
            }}
            onItemDelete={async (id: any) => {
              if (colId === 'material') {
                await ApiService.stockLookup.deleteMaterial(id)
              } else if (colId === 'format') {
                await ApiService.stockLookup.deleteFormat(id)
              } else if (colId === 'finishing') {
                await ApiService.stockLookup.deleteFinishing(id)
              } else if (colId === 'fabrication') {
                await ApiService.stockLookup.deleteFabrication(id)
              } else if (colId === 'heatTreatment') {
                await ApiService.stockLookup.deleteHeatTreatment(id)
              } else if (colId === 'originCountry') {
                await ApiService.stockLookup.deleteOriginCountry(id)
              } else if (colId === 'stockStreet') {
                await ApiService.stockLookup.deleteStockStreet(id)
              } else if (colId === 'shelf') {
                await ApiService.stockLookup.deleteShelf(id)
              } else if (colId === 'stockLocation') {
                await ApiService.stockLookup.deleteStockLocation(id)
              } else if (colId === 'family') {
                await ApiService.stockLookup.deleteFamily(id)
              } else if (colId === 'heatNumber') {
                await ApiService.stockLookup.deleteHeatNumber(id)
              } else if (colId === 'color') {
                await ApiService.resources.deleteColor(id)
              } else if (colId === 'stockStatus') {
                await ApiService.status.deleteStatus(id)
              } else if (colId === 'trackingNumber') {
                await ApiService.delete(`/backend/trackingNumber/${id}`)
              } else if (colId === 'purchaseOrder') {
                try {
                  await ApiService.delete(`/backend/purchaseOrder/${id}`)
                } catch (_err) {
                  void _err
                }
              }
            }}
            allowCreate={!isSupplierField}
            allowDelete={!isSupplierField}
            showCreateOnlyIfNotExists={true}
            minCreateLength={2}
          />
          {isSupplierField && rawSupplierList.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', maxHeight: '60px', overflowY: 'auto' }}>
              {rawSupplierList.map((sup, idx) => (
                <span
                  key={idx}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '3px',
                    backgroundColor: '#e0f2fe',
                    color: '#0369a1',
                    fontSize: '10px',
                    fontWeight: 500,
                    padding: '1px 5px',
                    borderRadius: '10px',
                    border: '1px solid #bae6fd',
                  }}
                >
                  {sup.name}
                  <X
                    size={10}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      const nextList = rawSupplierList.filter((_, i) => i !== idx)
                      setBulkDraft((prev) => ({
                        ...prev,
                        [colId]: nextList.map((s) => s.name).join(', '),
                        suppliers: nextList,
                      }))
                    }}
                  />
                </span>
              ))}
            </div>
          )}
        </div>
      )
    }

    const isDateField =
      colId.toLowerCase().includes('date') ||
      colId.toLowerCase().includes('data') ||
      colId.toLowerCase().endsWith('at') ||
      colId === 'receivedDate'

    if (isDateField) {
      const formatDateValueForInput = (raw: any): string => {
        if (!raw) return ''
        try {
          const d = new Date(raw)
          if (!isNaN(d.getTime())) {
            return d.toISOString().split('T')[0]
          }
        } catch (_err) {
          void _err
        }
        if (typeof raw === 'string' && raw.match(/^\d{4}-\d{2}-\d{2}/)) {
          return raw.slice(0, 10)
        }
        return String(raw)
      }

      return (
        <input
          type="date"
          value={formatDateValueForInput(value)}
          onChange={(e) => setBulkDraft((prev) => ({ ...prev, [colId]: e.target.value }))}
          style={{
            width: '100%',
            padding: '4px 6px',
            fontSize: '11px',
            borderRadius: '4px',
            border: '1px solid #0090d3',
            backgroundColor: '#fff',
            boxSizing: 'border-box',
            cursor: 'pointer',
          }}
        />
      )
    }

    return (
      <input
        type="text"
        placeholder="Alterar..."
        value={value}
        onChange={(e) => setBulkDraft((prev) => ({ ...prev, [colId]: e.target.value }))}
        style={{
          width: '100%',
          padding: '4px 6px',
          fontSize: '11px',
          borderRadius: '4px',
          border: '1px solid #0090d3',
          backgroundColor: '#fff',
          boxSizing: 'border-box',
        }}
      />
    )
  }

  const visibleColumns = useMemo(
    () => orderVisibleColumns(columns, columnsState),
    [columns, columnsState],
  )

  const bulkHeaderRow = selectedStockIds.size > 0 ? (
    <BulkHeaderLine>
      <BulkHeaderTd style={{ width: '40px', padding: '4px', textAlign: 'center' }}>
        <span style={{ fontSize: '11px', fontWeight: 600, color: '#0090d3' }}>Lote</span>
      </BulkHeaderTd>
      {visibleColumns.map((col) => (
        <BulkHeaderTd key={col.id} style={{ padding: '4px 6px' }}>
          {renderBulkInput(col.id)}
        </BulkHeaderTd>
      ))}
      <BulkHeaderTd style={{ padding: '4px 6px', textAlign: 'center' }}>
        <RowActionsMenu
          actions={[
            ...(isFashion
              ? [
                  {
                    label: 'Inativar em massa',
                    icon: <Power size={16} />,
                    onClick: () => setBulkInactivateOpen(true),
                  },
                ]
              : []),
            {
              label: 'Excluir em massa',
              icon: <Trash2 size={16} />,
              danger: true,
              onClick: () => setBulkDeleteOpen(true),
            },
          ]}
        />
      </BulkHeaderTd>
      <BulkHeaderTd style={{ padding: '4px' }} />
    </BulkHeaderLine>
  ) : null

  const toolbarLeft = selectedStockIds.size > 0 ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <span
        style={{
          backgroundColor: '#0090d3',
          color: '#fff',
          fontSize: '12px',
          fontWeight: 600,
          padding: '4px 12px',
          borderRadius: '16px',
        }}
      >
        {selectedStockIds.size} estoques selecionados
      </span>

      <button
        type="button"
        onClick={handleSaveBulkEdits}
        disabled={isBulkSaving}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 14px',
          borderRadius: '6px',
          backgroundColor: '#10b981',
          color: '#fff',
          border: 'none',
          fontSize: '12px',
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        <Check size={14} />
        {`Salvar ${selectedStockIds.size} Estoque(s) Editado(s)`}
      </button>

      <button
        type="button"
        onClick={() => {
          setSelectedStockIds(new Set())
          setBulkDraft({})
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '6px 12px',
          borderRadius: '6px',
          backgroundColor: '#f3f4f6',
          color: '#374151',
          border: '1px solid #d1d5db',
          fontSize: '12px',
          fontWeight: 500,
          cursor: 'pointer',
        }}
      >
        <X size={14} />
        Limpar Seleção
      </button>
    </div>
  ) : null

  return (
    <>
      {isBulkSaving && <LoadingComponent />}
      <StandardDataTable
      tableName='Stock'
      columns={columns}
      data={stockList}
      columnsState={columnsState}
      onColumnsChange={handleColumnsChange}
      onRestoreColumns={handleRestoreColumns}
      totalCount={totalStockCount ?? null}
      onLoadMore={() => setQuantity((prev) => prev + 50)}
      hasMore={hasMoreStock}
      isLoading={isLoading}
      selectable
      selectedRowIds={selectedStockIds}
      onSelectRow={handleSelectRow}
      onSelectAllRows={handleSelectAllRows}
      bulkHeaderRow={bulkHeaderRow}
      toolbarLeft={toolbarLeft}
      exportFileName={isFashion ? 'tecidos' : 'estoque'}
      exportSheetName={isFashion ? 'Tecidos' : 'Estoque'}
      exportRows={fetchAllStockRows}
      exportAllRows={fetchAllStockRecords}
      onBulkDelete={() => setBulkDeleteOpen(true)}
      onBulkInactivate={isFashion ? () => setBulkInactivateOpen(true) : undefined}
      rowClassName={(item) => (item.isActivated === false ? 'yellowLine' : '')}
      rowActions={(item) => [
        { label: 'Visualizar', icon: <Eye size={16} />, onClick: () => handleNewModal(item) },
        {
          label: 'Editar',
          icon: <Pencil size={16} />,
          onClick: () => handleEdit(item),
          disabled: item.isActivated === false,
          title: item.isActivated === false ? 'Item inativo' : undefined,
        },
        {
          label: 'Criar Variação',
          icon: <Copy size={16} />,
          onClick: () => handleCreateVariation(item),
          disabled: item.isActivated === false,
          title: item.isActivated === false ? 'Item inativo' : undefined,
        },
        {
          label: 'Novo movimento',
          icon: <ArrowLeftRight size={16} />,
          onClick: () => handleNewMovement(item),
          disabled: item.isActivated === false,
          title: item.isActivated === false ? 'Item inativo' : undefined,
        },
        {
          label: item.isActivated === false ? 'Ativar' : 'Inativar',
          icon: <Power size={16} />,
          onClick: () => handleSwitchActivation(item),
        },
        {
          label: 'Excluir',
          icon: <Trash2 size={16} />,
          onClick: () => setDeleteTarget(item),
        },
      ]}
    />

    <ConfirmationModal
      isOpen={!!deleteTarget}
      title='Excluir item de estoque'
      message={
        <div>
          <p>Tem certeza que deseja excluir o item de estoque &quot;{deleteTarget?.code || deleteTarget?.description || `#${deleteTarget?.id}`}&quot;? Esta ação não pode ser desfeita.</p>
        </div>
      }
      actions={[
        { label: 'Cancelar', onClick: () => setDeleteTarget(null), variant: 'outline' },
        {
          label: 'Excluir',
          onClick: handleConfirmDelete,
          variant: 'danger',
        },
      ]}
    />

    <ConfirmationModal
      isOpen={bulkDeleteOpen}
      title='Excluir itens em massa'
      message={
        <div>
          <p>Tem certeza que deseja excluir os {selectedStockIds.size} itens de estoque selecionados? Esta ação não pode ser desfeita.</p>
        </div>
      }
      actions={[
        { label: 'Cancelar', onClick: () => setBulkDeleteOpen(false), variant: 'outline' },
        {
          label: 'Excluir',
          onClick: handleConfirmBulkDelete,
          variant: 'danger',
        },
      ]}
    />

    <ConfirmationModal
      isOpen={bulkInactivateOpen}
      title='Inativar itens em massa'
      message={
        <div>
          <p>Tem certeza que deseja inativar os {selectedStockIds.size} itens de estoque selecionados?</p>
        </div>
      }
      actions={[
        { label: 'Cancelar', onClick: () => setBulkInactivateOpen(false), variant: 'outline' },
        {
          label: 'Inativar',
          onClick: handleConfirmBulkInactivate,
          variant: 'danger',
        },
      ]}
    />
    </>
  )
}

export default StockTable
